import { renderAsync } from 'docx-preview';
import { FC, useEffect, useRef, useState } from 'react';

import Loader from '../../../../components/loader/index';

/**
 * A Word document, drawn in the page — 20.2.3.
 *
 * `docx-preview` reads the document's own OOXML styles and lays it out page by page, which is why
 * nothing is converted on a server: the renderer the document templates screen already uses in
 * this app is enough here too, and the file never leaves the browser to be read.
 *
 * It is handed the bytes rather than a URL onto them. Fetching the object URL back was a round
 * trip through a resource somebody else owns: the viewer revokes it the moment the reader moves
 * on, and a revoked URL in flight is a failed fetch — of a file that had already arrived.
 *
 * Zoom scales the rendered result rather than re-rendering it. The library lays out at the
 * document's real page width, so a transform is both exact and instant, where re-rendering at
 * each step would re-parse the file for every press of the button.
 */
export const FilePreviewDocx: FC<{ blob: Blob; onError: () => void; zoom: number }> = ({
  blob,
  onError,
  zoom,
}) => {
  const container = useRef<HTMLDivElement>(null);
  const [isRendering, setIsRendering] = useState(true);

  useEffect(() => {
    const target = container.current;

    if (!target) return;

    let isCurrent = true;

    setIsRendering(true);

    void renderAsync(blob, target, undefined, {
      className: 'docx-preview',
      ignoreLastRenderedPageBreak: false,
      inWrapper: true,
    })
      // A document the library cannot lay out is a failure to say out loud: the screen offers the
      // download instead, where a spinner that never stops offers nothing.
      .catch(() => {
        if (isCurrent) onError();
      })
      .finally(() => {
        if (isCurrent) setIsRendering(false);
      });

    return () => {
      isCurrent = false;
      target.replaceChildren();
    };
  }, [blob, onError]);

  return (
    <div className="h-fit w-fit">
      {isRendering && <Loader />}
      <div
        ref={container}
        style={{ transform: `scale(${zoom / 100})`, transformOrigin: 'top center' }}
      />
    </div>
  );
};
